import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Grid
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, LockReset } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';

interface TokenVerification {
    valid: boolean;
    message: string;
    userId: string;
    email: string;
}

const ResetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenData, setTokenData] = useState<TokenVerification | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [resendEmail, setResendEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMessage, setResendMessage] = useState<string | null>(null);
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('Invalid reset link. No token provided.');
            setLoading(false);
            return;
        }
        
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            setVerifying(true);
            const response = await api.get(`/api/auth/reset-password/verify?token=${token}`);
            
            if (response.data.valid) {
                setTokenValid(true);
                setTokenData(response.data);
            } else {
                setError('Invalid or expired reset token.');
            }
        } catch (error: any) {
            setError(error?.response?.data?.error || 'Failed to verify reset token.');
        } finally {
            setVerifying(false);
            setLoading(false);
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!password || !confirmPassword) {
            setError('Please fill in all fields.');
            return;
        }
        
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        
        if (password.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }
        
        try {
            setResetting(true);
            setError(null);
            
            const response = await api.post('/api/auth/reset-password', {
                token: token,
                newPassword: password
            });
            
            setSuccess('Password has been reset successfully! You can now sign in with your new password.');
            
            // Redirect to login page after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
            
        } catch (error: any) {
            setError(error?.response?.data?.error || 'Failed to reset password. Please try again.');
        } finally {
            setResetting(false);
        }
    };

    if (loading || verifying) {
        return (
            <Box 
                sx={{ 
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4
                }}
            >
                <Container maxWidth="sm">
                    <Paper 
                        elevation={24} 
                        sx={{ 
                            p: { xs: 3, sm: 4, md: 5 },
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            textAlign: 'center'
                        }}
                    >
                        <CircularProgress size={60} sx={{ color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Verifying reset token...
                        </Typography>
                    </Paper>
                </Container>
            </Box>
        );
    }

    if (!tokenValid) {
        return (
            <Box 
                sx={{ 
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4
                }}
            >
                <Container maxWidth="sm">
                    <Paper 
                        elevation={24} 
                        sx={{ 
                            p: { xs: 3, sm: 4, md: 5 },
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                    >
                        {/* SMEDCO Logo and Header */}
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box 
                                component="img"
                                src="/logo.png"
                                alt="SMEDCO Logo"
                                sx={{ 
                                    height: 80,
                                    width: 'auto',
                                    mb: 2,
                                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                                }}
                            />
                            <Typography 
                                variant="h4" 
                                component="h1" 
                                sx={{ 
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 1
                                }}
                            >
                                This reset link has expired
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                                For your security, reset links expire after a short time. You can request a new one below.
                            </Typography>
                        </Box>

                        {error && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mb: 3,
                                    borderRadius: 2,
                                    '& .MuiAlert-icon': { fontSize: '1.5rem' }
                                }}
                            >
                                {error}
                            </Alert>
                        )}

                        {/* Request new link inline */}
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                Send me a new reset link
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        fullWidth
                                        placeholder="Email address"
                                        value={resendEmail}
                                        onChange={(e) => setResendEmail(e.target.value)}
                                        size="medium"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock sx={{ color: 'primary.main' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disabled={resendLoading || !resendEmail.trim()}
                                        onClick={async () => {
                                            try {
                                                setResendMessage(null);
                                                setResendLoading(true);
                                                await api.post('/api/auth/forgot-password/email', { usernameOrEmail: resendEmail.trim() });
                                                setResendMessage('If the account exists, a new reset link has been sent.');
                                            } catch (e) {
                                                setResendMessage('If the account exists, a new reset link has been sent.');
                                            } finally {
                                                setResendLoading(false);
                                            }
                                        }}
                                        sx={{
                                            py: 1.2,
                                            borderRadius: 2,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                        }}
                                    >
                                        {resendLoading ? <CircularProgress size={20} color="inherit" /> : 'Send link'}
                                    </Button>
                                </Grid>
                            </Grid>
                            {resendMessage && (
                                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                                    {resendMessage}
                                </Alert>
                            )}
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/login')}
                            startIcon={<Lock />}
                            sx={{
                                py: 1.5,
                                borderRadius: 2,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #152a5a 0%, #1e3c72 100%)',
                                    boxShadow: '0 12px 35px rgba(30, 60, 114, 0.4)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            Back to Login
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box 
            sx={{ 
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper 
                    elevation={24} 
                    sx={{ 
                        p: { xs: 3, sm: 4, md: 5 },
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                >
                    {/* SMEDCO Logo and Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box 
                            component="img"
                            src="/logo.png"
                            alt="SMEDCO Logo"
                            sx={{ 
                                height: 80,
                                width: 'auto',
                                mb: 2,
                                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
                            }}
                        />
                        <Typography 
                            variant="h4" 
                            component="h1" 
                            sx={{ 
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 1
                            }}
                        >
                            Reset Your Password
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                            {tokenData ? `Reset password for: ${tokenData.email}` : 'Enter your new password below'}
                        </Typography>
                    </Box>

                    {/* Alerts */}
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                borderRadius: 2,
                                '& .MuiAlert-icon': { fontSize: '1.5rem' }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert 
                            severity="success" 
                            sx={{ 
                                mb: 3,
                                borderRadius: 2,
                                '& .MuiAlert-icon': { fontSize: '1.5rem' }
                            }}
                        >
                            {success}
                        </Alert>
                    )}

                    {/* Reset Password Form */}
                    <Box component="form" onSubmit={handleResetPassword}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    variant="outlined"
                                    size="large"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: 'primary.main' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    label="Confirm New Password"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    fullWidth
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    variant="outlined"
                                    size="large"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: 'primary.main' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            '&:hover fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={resetting}
                                    startIcon={resetting ? <CircularProgress size={20} color="inherit" /> : <LockReset />}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        fontSize: '1.1rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                        boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #152a5a 0%, #1e3c72 100%)',
                                            boxShadow: '0 12px 35px rgba(30, 60, 114, 0.4)',
                                            transform: 'translateY(-2px)',
                                        },
                                        '&:disabled': {
                                            background: 'linear-gradient(135deg, #8fa3c7 0%, #9bb3d1 100%)',
                                            transform: 'none',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {resetting ? 'Resetting Password...' : 'Reset Password'}
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ textAlign: 'center', mt: 1 }}>
                                    <Button 
                                        variant="text" 
                                        size="small" 
                                        onClick={() => navigate('/login')}
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: 'rgba(30, 60, 114, 0.04)',
                                            }
                                        }}
                                    >
                                        Back to Login
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResetPassword;
